import React, { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { Scale, LayoutDashboard, Building2, Users, LogOut, Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/platform", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/platform/organizations", label: "Organizations", icon: Building2 },
  { to: "/platform/users", label: "Users", icon: Users },
];

const linkClass = ({ isActive }) =>
  `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors ${
    isActive ? "bg-white/10 text-white" : "text-slate-300 hover:bg-white/5 hover:text-white"
  }`;

const Brand = () => (
  <Link to="/platform" data-testid="owner-brand" className="flex items-center gap-3">
    <span className="flex h-9 w-9 items-center justify-center rounded-md bg-white/10 text-gold">
      <Scale className="h-5 w-5" strokeWidth={1.75} />
    </span>
    <span className="leading-tight">
      <span className="block font-display text-lg text-white">Advocate Diary</span>
      <span className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-gold">
        Owner / Admin
      </span>
    </span>
  </Link>
);

export default function OwnerLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const signOut = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div data-testid="owner-layout" className="flex min-h-screen bg-[#F7F9FC]">
      <aside className="hidden w-60 shrink-0 flex-col bg-navy p-4 lg:flex">
        <Brand />
        <nav className="mt-8 flex flex-1 flex-col gap-1">
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end} className={linkClass}
                     data-testid={`owner-nav-${label.toLowerCase()}`}>
              <Icon className="h-4 w-4" /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-white/10 pt-3">
          <p className="px-3 pb-2 text-xs text-slate-400">{user?.email}</p>
          <button type="button" onClick={signOut} data-testid="owner-logout-btn"
                  className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm text-slate-300 hover:bg-white/5 hover:text-white">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center justify-between border-b border-white/10 bg-navy px-4 py-3 lg:hidden">
          <Brand />
          <button type="button" onClick={() => setOpen((v) => !v)} aria-label="Toggle menu"
                  data-testid="owner-mobile-toggle" className="rounded-md p-2 text-white">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </header>
        {open && (
          <div data-testid="owner-mobile-menu" className="border-b border-white/10 bg-navy px-4 py-3 lg:hidden">
            {NAV.map(({ to, label, icon: Icon, end }) => (
              <NavLink key={to} to={to} end={end} onClick={() => setOpen(false)} className={linkClass}
                       data-testid={`owner-mobile-nav-${label.toLowerCase()}`}>
                <Icon className="h-4 w-4" /> {label}
              </NavLink>
            ))}
            <button type="button" onClick={signOut} data-testid="owner-mobile-logout-btn"
                    className="mt-1 flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm text-slate-300">
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        )}
        <main className="min-w-0 flex-1 px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
